import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import type { ArtistReleasesResponse, ArtistSummary, SearchResponse } from '../../shared/types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly retryAfter?: number,
    /** False when the response didn't come from our proxy (offline, or no /api server). */
    readonly fromProxy = true,
  ) {
    super(message);
  }
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { signal, headers: { Accept: 'application/json' } });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'You appear to be offline.', undefined, false);
  }
  const isJson = res.headers.get('Content-Type')?.includes('application/json');
  if (!res.ok || !isJson) {
    const body = isJson ? ((await res.json()) as { error?: string; retryAfter?: number }) : {};
    throw new ApiError(res.status, body.error ?? 'Something went wrong.', body.retryAfter, isJson);
  }
  return (await res.json()) as T;
}

/**
 * In `vite dev` without `wrangler pages dev` running, /api isn't there. Fall back to the same
 * fixture data the proxy serves, so the UI is usable on its own. `import.meta.env.DEV` is a
 * build-time constant, so production builds drop this and the fixture chunk.
 */
const devData = import.meta.env.DEV ? () => import('../../functions/_lib/data') : null;

async function withDevFallback<T>(
  load: () => Promise<T>,
  fixture: (data: typeof import('../../functions/_lib/data')) => Promise<T>,
): Promise<T> {
  try {
    return await load();
  } catch (err) {
    const proxyMissing = err instanceof ApiError && !err.fromProxy;
    if (devData && proxyMissing) return fixture(await devData());
    throw err;
  }
}

export function searchArtists(q: string, signal?: AbortSignal) {
  return withDevFallback(
    () => getJson<SearchResponse>(`/api/search?q=${encodeURIComponent(q)}`, signal),
    (d) => d.searchArtists({}, q),
  );
}

export function getArtist(id: string, signal?: AbortSignal) {
  return withDevFallback(
    () => getJson<ArtistSummary>(`/api/artist/${encodeURIComponent(id)}`, signal),
    (d) => d.getArtist({}, id),
  );
}

export function getReleases(id: string, signal?: AbortSignal) {
  return withDevFallback(
    () => getJson<ArtistReleasesResponse>(`/api/artist/${encodeURIComponent(id)}/releases`, signal),
    (d) => d.getReleases({}, id, 'US'),
  );
}

function shouldRetry(count: number, err: unknown) {
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
  return count < 1;
}

function retryDelay(_count: number, err: unknown) {
  const s = err instanceof ApiError && err.retryAfter ? Math.min(err.retryAfter, 10) : 1;
  return s * 1000;
}

/** Shared by the search box and the paste-a-list importer, so they share a cache. */
export function artistSearchOptions(q: string) {
  const query = q.trim();
  return queryOptions({
    queryKey: ['search', query.toLowerCase()],
    queryFn: ({ signal }) => searchArtists(query, signal),
    staleTime: 5 * 60_000,
    retry: shouldRetry,
    retryDelay,
  });
}

export function useArtistSearch(q: string) {
  return useQuery({ ...artistSearchOptions(q), enabled: q.trim().length > 0, placeholderData: keepPreviousData });
}

export function useArtist(id: string | undefined) {
  return useQuery({
    queryKey: ['artist', id],
    queryFn: ({ signal }) => getArtist(id!, signal),
    enabled: Boolean(id),
    staleTime: 60 * 60_000,
    retry: shouldRetry,
    retryDelay,
  });
}

export function useReleases(id: string | undefined) {
  return useQuery({
    queryKey: ['releases', id],
    queryFn: ({ signal }) => getReleases(id!, signal),
    enabled: Boolean(id),
    staleTime: 60 * 60_000,
    retry: shouldRetry,
    retryDelay,
  });
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return 'We couldn’t find that artist.';
    if (err.status === 503) return 'Spotify is busy right now. Try again in a moment.';
    return err.message;
  }
  return 'Something went wrong.';
}
